import { z } from 'zod';
/**
 * Public contract for the sole adapter allowed to access a SAD/sadLegacy
 * MySQL domain schema.  It deliberately exposes legacy numeric identifiers
 * instead of pretending that they are SAD5 identities.
 */
export const legacyBridgeContractVersionSchema = z.literal('1');
const legacyIdSchema = z.number().int().positive();
const dateSchema = z.string().date();
const dateTimeSchema = z.string().datetime({ offset: true });
const legacySchemaColumnFingerprintSchema = z.object({
    columnName: z.string().min(1),
    dataType: z.string().min(1),
    columnType: z.string().min(1),
    nullable: z.boolean(),
    characterSet: z.string().min(1).nullable(),
    collation: z.string().min(1).nullable(),
});
const legacySchemaIndexFingerprintSchema = z.object({
    indexName: z.string().min(1),
    isUnique: z.boolean(),
    indexType: z.string().min(1),
    columns: z.array(z.string().min(1)).min(1),
});
const legacySchemaTableFingerprintSchema = z.object({
    tableName: z.string().min(1),
    engine: z.string().min(1).nullable(),
    characterSet: z.string().min(1).nullable(),
    collation: z.string().min(1).nullable(),
    columns: z.array(legacySchemaColumnFingerprintSchema),
    indexes: z.array(legacySchemaIndexFingerprintSchema),
});
export const legacySchemaFingerprintSchema = z.object({
    schemaCharacterSet: z.string().min(1).nullable(),
    schemaCollation: z.string().min(1).nullable(),
    tables: z.array(legacySchemaTableFingerprintSchema),
    optionalTableFamilies: z.array(z.object({
        id: z.string().min(1),
        tables: z.array(z.string().min(1)).min(1),
        detected: z.boolean(),
        missingTables: z.array(z.string().min(1)),
    })),
    writeTransactionality: z.object({
        state: z.enum(['eligible', 'blocked_non_innodb', 'blocked_unknown_engine']),
        writerTables: z.array(z.string().min(1)).min(1),
        nonInnoDbTables: z.array(z.string().min(1)),
        unknownEngineTables: z.array(z.string().min(1)),
        summary: z.string().min(1).max(500),
    }),
});
export const legacyBridgeCapabilitySchema = z.object({
    profile: z.literal('sadLegacy.ts_prowiant.v1'),
    contractVersion: legacyBridgeContractVersionSchema,
    mode: z.enum(['read_only', 'write_enabled']),
    available: z.boolean(),
    requiredTables: z.array(z.string()),
    missingTables: z.array(z.string()),
    missingColumns: z.array(z.string()),
    /** Optional for older Bridge v1 deployments; new preflight requires it. */
    schemaFingerprint: legacySchemaFingerprintSchema.optional(),
    checkedAt: dateTimeSchema,
});
/** Safe status exposed to SAD5 products; the service token never leaves a BFF. */
export const legacyBridgeIntegrationStatusSchema = z
    .object({
    contractVersion: legacyBridgeContractVersionSchema,
    integration: z.literal('sadLegacy'),
    state: z.enum(['disabled', 'available', 'incompatible', 'unavailable']),
    observedAt: dateTimeSchema,
    capability: legacyBridgeCapabilitySchema.nullable(),
    unavailableReason: z.string().min(1).max(500).nullable(),
})
    .superRefine((value, context) => {
    if (value.state === 'available' && value.capability?.available !== true)
        context.addIssue({
            code: 'custom',
            path: ['capability'],
            message: 'Dostępna integracja wymaga zgodnego profilu legacy.',
        });
    if (['disabled', 'unavailable'].includes(value.state) &&
        value.unavailableReason === null)
        context.addIssue({
            code: 'custom',
            path: ['unavailableReason'],
            message: 'Niedostępna integracja musi podać bezpieczny powód.',
        });
});
/**
 * Internal-only contract used by the Keycloak SAD2 federation adapter.
 * The bridge never exposes a legacy hash, salt, account state or a reason for
 * a failed sign-in.
 */
export const legacyPasswordVerificationRequestSchema = z.object({
    login: z.string().trim().min(1).max(50),
    password: z.string().min(1).max(256),
});
export const legacyPasswordVerificationResultSchema = z
    .object({
    authenticated: z.boolean(),
    legacySubject: z
        .string()
        .regex(/^sad2:[1-9][0-9]*$/)
        .nullable(),
})
    .superRefine((value, context) => {
    if (value.authenticated !== (value.legacySubject !== null))
        context.addIssue({
            code: 'custom',
            path: ['legacySubject'],
            message: 'Udane sprawdzenie musi zwrócić podmiot SAD2; nieudane nie może go ujawnić.',
        });
});
/**
 * Internal-only lookup used exclusively by the Keycloak SAD2 user-storage
 * provider. The result deliberately omits e-mail, names, account state,
 * password material and failure reasons.
 */
export const legacyIdentityLookupRequestSchema = z
    .object({
    login: z.string().trim().min(1).max(50).optional(),
    legacySubject: z
        .string()
        .regex(/^sad2:[1-9][0-9]*$/)
        .optional(),
})
    .superRefine((value, context) => {
    if ((value.login === undefined) === (value.legacySubject === undefined))
        context.addIssue({
            code: 'custom',
            message: 'Należy podać dokładnie login albo legacySubject.',
        });
});
export const legacyIdentityLookupResultSchema = z
    .object({
    legacySubject: z
        .string()
        .regex(/^sad2:[1-9][0-9]*$/)
        .nullable(),
    login: z.string().trim().min(1).max(50).nullable(),
})
    .superRefine((value, context) => {
    if ((value.legacySubject === null) !== (value.login === null))
        context.addIssue({
            code: 'custom',
            message: 'Wynik lookupu musi zwrócić komplet tożsamości albo null.',
        });
});
/**
 * Bounded, keyset-paginated projection used only to prepare SAD2 accounts for
 * IAM migration.  It is intentionally not an authentication or Keycloak
 * contract and must never grow password, token, state, or permission fields.
 */
export const legacySad2AccountMigrationQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(100),
    afterLegacyUserId: z.coerce.number().int().positive().optional(),
});
/** Legacy ACL groups are exported as migration input, not interpreted as IAM roles. */
export const legacySad2AccountMigrationGroupSchema = z.object({
    legacyGroupId: legacyIdSchema,
    name: z.string().trim().min(1).max(160),
});
export const legacySad2AccountMigrationAccountSchema = z.object({
    legacySubject: z.string().regex(/^sad2:[1-9][0-9]*$/),
    login: z.string().trim().min(1).max(50),
    givenName: z.string().trim().min(1).max(100).nullable(),
    familyName: z.string().trim().min(1).max(100).nullable(),
    email: z.email().nullable(),
    groups: z.array(legacySad2AccountMigrationGroupSchema),
    /** Content and allow/deny semantics stay in legacy until IAM maps them. */
    hasIndividualPermissions: z.boolean(),
});
export const legacySad2AccountMigrationPageSchema = z.object({
    contractVersion: legacyBridgeContractVersionSchema,
    items: z.array(legacySad2AccountMigrationAccountSchema).max(200),
    /** Opaque traversal boundary represented by the last legacy numeric ID. */
    nextAfterLegacyUserId: legacyIdSchema.nullable(),
});
/**
 * Exact schema profile for the migration projection. Both legacy ACL families
 * are required so the direct-permission indicator cannot silently be false.
 */
export const sadLegacySad2AccountMigrationV1Profile = {
    id: 'sadLegacy.sad2-account-migration.v1',
    requiredTables: [
        'ts_uzyt',
        'ts_grupy_uprawnien',
        'ts_uzyt_gr_upr',
        'ts_uzyt_upr',
        'ts_upr_uzyt',
    ],
    requiredColumns: {
        ts_uzyt: [
            'id_uzyt',
            'login',
            'imie',
            'nazwisko',
            'email',
            'czy_aktywne',
            'czy_zablokowane',
            'data_wygas_konta',
        ],
        ts_grupy_uprawnien: ['id_grupy_uprawnien', 'nazwa'],
        ts_uzyt_gr_upr: ['fk_gr_upr', 'fk_uzyt'],
        ts_uzyt_upr: ['fk_uzyt'],
        ts_upr_uzyt: ['fk_uzyt'],
    },
};
export const legacyCanteenPeriodSchema = z.object({
    legacyPeriodId: legacyIdSchema,
    name: z.string().nullable(),
    startsOn: dateSchema.nullable(),
    endsOn: dateSchema.nullable(),
    orderingStartsOn: dateSchema.nullable(),
    orderingEndsOn: dateSchema.nullable(),
});
export const legacyCanteenMenuItemSchema = z.object({
    legacyMenuItemId: legacyIdSchema,
    legacyMealKindId: legacyIdSchema,
    servesOn: dateSchema.nullable(),
    description: z.string().nullable(),
    information: z.string().nullable(),
});
export const legacyCanteenMealSchema = z.object({
    legacyMealId: legacyIdSchema,
    legacyUserId: legacyIdSchema,
    legacyPeriodId: legacyIdSchema,
    legacyMealKindId: legacyIdSchema,
    legacyCurrentStateId: legacyIdSchema,
    legacyDesiredStateId: legacyIdSchema,
    servesOn: dateSchema.nullable(),
    priceMinor: z.number().int().nullable(),
    requiresApproval: z.boolean(),
});
export const legacyDateRangeQuerySchema = z
    .object({ from: dateSchema, to: dateSchema })
    .refine((value) => value.from <= value.to, {
    path: ['to'],
    message: 'Koniec zakresu nie może być wcześniejszy niż początek.',
});
export const legacyMealsQuerySchema = legacyDateRangeQuerySchema.extend({
    legacyUserId: z.coerce.number().int().positive(),
});
export const legacyReservationCommandSchema = z.object({
    contractVersion: legacyBridgeContractVersionSchema,
    action: z.enum(['reserve', 'cancel']),
    legacyUserId: legacyIdSchema,
    legacyPeriodId: legacyIdSchema,
    legacyMealKindId: legacyIdSchema,
    servesOn: dateSchema,
    /** Decimal legacy prices are converted only by the bridge at its boundary. */
    priceMinor: z.number().int().min(0).max(10_000_000),
    requiresApproval: z.boolean().default(false),
});
export const legacyCommandReceiptSchema = z.object({
    contractVersion: legacyBridgeContractVersionSchema,
    command: z.literal('canteen.reservation'),
    idempotencyKey: z.string().min(16).max(200),
    outcome: z.enum([
        'created',
        'already_present',
        'cancelled',
        'already_absent',
    ]),
    legacyMealId: legacyIdSchema.nullable(),
    completedAt: dateTimeSchema,
});
/** The concrete SAD/sadLegacy profile, taken from dbo_ts_prowiant*.php. */
export const sadLegacyTsProwiantV1Profile = {
    id: 'sadLegacy.ts_prowiant.v1',
    requiredTables: ['ts_prowiant', 'ts_prowiant_okres', 'ts_prowiant_jadlospis'],
    optionalTables: [
        'ts_prowiant_cena',
        'ts_prowiant_okres_wylaczenie',
        'ts_prowiant_podop_uzyt',
        'ts_prowiant_podop_grupa',
        'ts_prowiant_uzyt_konfig',
        'ts_prowiant_indeks_mat',
        'ts_prowiant_magazyn',
        'ts_prowiant_zapotrzebowanie',
        'ts_prowiant_zapotrzebowanie_pozycja',
        'ts_prowiant_faktura',
        'ts_prowiant_faktura_pozycja',
    ],
    requiredColumns: {
        ts_prowiant: [
            'id_prowiant',
            'cena',
            'data',
            'czy_wymaga_akceptacji',
            'fk_prowiant_stan_aktualny',
            'fk_prowiant_stan_pozadany',
            'fk_uzyt',
            'fk_prowiant_okres',
            'fk_prowiant_rodzaj',
        ],
        ts_prowiant_okres: [
            'id_prowiant_okres',
            'nazwa',
            'data_od',
            'data_do',
            'prowiandowanie_od',
            'prowiantowanie_do',
        ],
        ts_prowiant_jadlospis: [
            'id_prowiant_jadlospisn',
            'data',
            'fk_rodzaj_posilku',
            'opis',
            'info',
        ],
    },
};
/**
 * These groups are evidence-only discovery targets. They never enable a
 * writer, export rows, or broaden the public API.
 */
export const sadLegacyTsProwiantV1OptionalTableFamilies = [
    { id: 'pricing', tables: ['ts_prowiant_cena'] },
    { id: 'period-exclusions', tables: ['ts_prowiant_okres_wylaczenie'] },
    {
        id: 'dependents',
        tables: ['ts_prowiant_podop_uzyt', 'ts_prowiant_podop_grupa'],
    },
    { id: 'user-configuration', tables: ['ts_prowiant_uzyt_konfig'] },
    {
        id: 'inventory',
        tables: ['ts_prowiant_indeks_mat', 'ts_prowiant_magazyn'],
    },
    {
        id: 'procurement-and-invoices',
        tables: [
            'ts_prowiant_zapotrzebowanie',
            'ts_prowiant_zapotrzebowanie_pozycja',
            'ts_prowiant_faktura',
            'ts_prowiant_faktura_pozycja',
        ],
    },
];
/** The only legacy domain table touched by the currently versioned writer. */
export const sadLegacyTsProwiantV1WriterTables = ['ts_prowiant'];
